import { useEffect, useState } from "react";
import { ordersApi, type WCOrder } from "../../../lib/api";

export default function BookingsTab() {
  const [orders, setOrders]   = useState<WCOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    ordersApi
      .list()
      .then(setOrders)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const statusColor: Record<string, string> = {
    pending:    "bg-amber-100 text-amber-700 border-amber-200",
    processing: "bg-blue-100 text-blue-700 border-blue-200",
    completed:  "bg-green-100 text-green-700 border-green-200",
    cancelled:  "bg-red-100 text-red-700 border-red-200",
    "on-hold":  "bg-gray-100 text-gray-600 border-gray-200",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 rounded-full border-4 border-cl-green border-t-transparent animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-3xl cartoon-card p-8 text-center">
        <i className="ri-error-warning-line text-4xl text-red-400 mb-3 block"></i>
        <p className="text-gray-600 font-semibold text-sm">{error}</p>
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div className="bg-white rounded-3xl cartoon-card p-10 text-center">
        <div className="w-16 h-16 bg-cl-tint rounded-2xl flex items-center justify-center mx-auto mb-5">
          <i className="ri-calendar-line text-cl-green text-3xl"></i>
        </div>
        <h3 className="font-black text-cl-dark text-lg mb-2">No bookings yet</h3>
        <p className="text-gray-500 text-sm font-semibold mb-6">When you book a collection, it will appear here.</p>
        <a href="/book" className="inline-flex items-center gap-2 bg-cl-green text-white font-black px-6 py-3 rounded-xl text-sm cartoon-btn-green">
          Book Your First Collection
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-black text-cl-dark">My Bookings</h2>
      {orders.map((order) => {
        const dateStr = order.booking_date
          ? new Date(order.booking_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" })
          : order.date
            ? new Date(order.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
            : "—";

        const statusClass = statusColor[order.status] ?? "bg-gray-100 text-gray-600 border-gray-200";

        return (
          <div key={order.id} className="bg-white rounded-2xl cartoon-card p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div>
                <span className="text-xs font-black text-gray-400 uppercase tracking-widest">Order #{order.id}</span>
                <h3 className="font-black text-cl-dark text-base leading-tight">{order.service_slug?.replace(/-/g, " ") || "Collection"}</h3>
              </div>
              <span className={`inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full border-2 w-fit ${statusClass}`}>
                {order.status}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-0.5">Date</p>
                <p className="text-sm font-bold text-cl-dark">{dateStr}</p>
              </div>
              {order.time_label && (
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-0.5">Time Slot</p>
                  <p className="text-sm font-bold text-cl-dark">{order.time_label}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-0.5">Total</p>
                <p className="text-sm font-black text-cl-green">£{parseFloat(order.total).toFixed(2)}</p>
              </div>
            </div>

            {order.items?.length > 0 && (
              <div className="border-t-2 border-gray-100 pt-4">
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Items</p>
                <ul className="space-y-1">
                  {order.items.map((item, i) => (
                    <li key={i} className="flex justify-between text-sm">
                      <span className="text-cl-dark font-semibold">{item.name} {item.quantity > 1 ? `× ${item.quantity}` : ""}</span>
                      <span className="font-black text-cl-dark">£{parseFloat(item.total).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
